import { computed, makeObservable, observable } from 'mobx'
import Workspace from 'iam/models/Workspace'
import { PrimeProvider } from 'iam/ProviderStore'
import { PrimeUser } from 'iam/UserStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { collection } from 'infra/collection'
import { Model } from 'infra/model'
import { DateZero } from 'utils/date-util'
import { WorkspaceApplicantDTO } from './dtos/workspace-applicant'
import {
  WorkspacePostingDTO,
  WorkspacePostingStatusDTO,
  WorkspacePostingJobTypeDTO,
  WorkspacePostingOpportunityTypeDto,
} from './dtos/workspace-posting'
import WorkspaceApplicant from './WorkspaceApplicant'

export enum WorkspacePostingJobType {
  Prep = 'PREP',
  Review = 'REVIEW',
  Bookkeeping = 'BOOKKEEPING',
  Null = 'NULL',
}

export enum WorkspacePostingStatus {
  Draft = 'DRAFT',
  Active = 'ACTIVE',
  Closed = 'CLOSED',
  Cancelled = 'CANCELLED',
  InReview = 'IN_REVIEW',
  Null = 'NULL',
}

export enum WorkspacePostingOpportunityType {
  Unknown = 'UNKNOWN',
  SignAndFile = 'SIGN_AND_FILE',
  Outsourcing = 'OUTSOURCING',
}

export default class WorkspacePosting extends Model<
  WorkspacePostingDTO,
  WorkspacePosting
> {
  id: string = ''
  workspaceId: string = ''
  workspace: Workspace = Workspace.none
  dateCreated: Date = DateZero()
  dateModified: Date = DateZero()
  location: string = ''
  primarySoftware: string = ''
  supplementalSoftware: string = ''
  backupFileNeeded: boolean = false
  specialties: string[] = []
  capacity: number = 0
  maxAllowedWorkspaces: number = 0
  jobTypes: WorkspacePostingJobType[] = []
  description: string = ''
  opportunityType: WorkspacePostingOpportunityType =
    WorkspacePostingOpportunityType.Unknown

  status: WorkspacePostingStatus = WorkspacePostingStatus.Null
  applicants = collection<WorkspaceApplicantDTO, WorkspaceApplicant>({
    idAttribute: 'user_public_id',
    create: () =>
      new WorkspaceApplicant(
        this.workspaceId,
        this.primeUser,
        this.primeProvider,
      ),
  })

  constructor(
    private primeWorkspace: PrimeWorkspace,
    private primeUser: PrimeUser,
    private primeProvider: PrimeProvider,
  ) {
    super()
    makeObservable(this, {
      id: observable,
      workspaceId: observable,
      workspace: observable,
      dateCreated: observable,
      dateModified: observable,
      location: observable,
      primarySoftware: observable,
      supplementalSoftware: observable,
      backupFileNeeded: observable,
      specialties: observable,
      capacity: observable,
      maxAllowedWorkspaces: observable,
      jobTypes: observable,
      description: observable,
      status: observable,
      opportunityTypeTitle: computed,
    })
  }

  get opportunityTypeTitle() {
    switch (this.opportunityType) {
      case WorkspacePostingOpportunityType.Unknown:
        return 'Unknown'
      case WorkspacePostingOpportunityType.Outsourcing:
        return 'Outsourcing'
      case WorkspacePostingOpportunityType.SignAndFile:
        return 'Sign and File'
    }
  }

  parse(attrs: WorkspacePostingDTO) {
    return {
      id: attrs.id,
      workspaceId: attrs.workspace_id,
      workspace: attrs.workspace_id
        ? this.primeWorkspace({ id: attrs.workspace_id })
        : Workspace.none,
      dateCreated: attrs.date_created,
      dateModified: attrs.date_modified,
      location: attrs.location,
      primarySoftware: attrs.primary_software,
      supplementalSoftware: attrs.supplemental_software,
      backupFileNeeded: attrs.backup_file_needed,
      specialties: attrs.specialties,
      capacity: attrs.capacity,
      maxAllowedWorkspaces: attrs.max_allowed_workspaces,
      jobTypes: this.parseWorkspacePostingJobTypeDto(attrs.job_types),
      description: attrs.description,
      status: this.parseWorkspacePostingStatusDto(attrs.status),
      applicants: this.applicants.reset(attrs.applicants),
      opportunityType: this.parseWorkspacePostingOpportunityTypeDto(
        attrs.opportunity_type,
      ),
    }
  }

  parseWorkspacePostingJobTypeDto(
    dto: WorkspacePostingJobTypeDTO[],
  ): WorkspacePostingJobType[] {
    return dto.map((jobType) => {
      switch (jobType) {
        case WorkspacePostingJobTypeDTO.Prep:
          return WorkspacePostingJobType.Prep
        case WorkspacePostingJobTypeDTO.Review:
          return WorkspacePostingJobType.Review
        case WorkspacePostingJobTypeDTO.Bookkeeping:
          return WorkspacePostingJobType.Bookkeeping
        default:
          return WorkspacePostingJobType.Null
      }
    })
  }

  parseWorkspacePostingOpportunityTypeDto(
    dto: WorkspacePostingOpportunityTypeDto,
  ): WorkspacePostingOpportunityType {
    switch (dto) {
      case WorkspacePostingOpportunityTypeDto.Outsourcing:
        return WorkspacePostingOpportunityType.Outsourcing
      case WorkspacePostingOpportunityTypeDto.SignAndFile:
        return WorkspacePostingOpportunityType.SignAndFile
      default:
        return WorkspacePostingOpportunityType.Unknown
    }
  }

  parseWorkspacePostingStatusDto(
    dto: WorkspacePostingStatusDTO,
  ): WorkspacePostingStatus {
    switch (dto) {
      case WorkspacePostingStatusDTO.Draft:
        return WorkspacePostingStatus.Draft
      case WorkspacePostingStatusDTO.Active:
        return WorkspacePostingStatus.Active
      case WorkspacePostingStatusDTO.Closed:
        return WorkspacePostingStatus.Closed
      case WorkspacePostingStatusDTO.Cancelled:
        return WorkspacePostingStatus.Cancelled
      case WorkspacePostingStatusDTO.InReview:
        return WorkspacePostingStatus.InReview
      default:
        return WorkspacePostingStatus.Null
    }
  }
}
